import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { observable, action } from 'mobx';
import { Button, Intent, Spinner } from '@blueprintjs/core';
import moment from 'moment';
import { Api } from 'services/Api';
import Toast from 'helpers/toaster';
import { currencyFormattedAmount } from 'helpers/currency';

// Card payments on the admin booking page, for bookings paid by card on the
// public booking form: every charge and refund, the invoice they went onto
// when the booking was confirmed (and anything paid on the invoice since),
// refunds (admins), and the payment log.
//   GET  /bookings/:id/payments  { amount_paid_cents, refundable_cents, payments, events, invoice }
//   POST /bookings/:id/refund    { amount_cents, reason, request_key }
// Styles: .bk-payments in styles/booking-form.scss.

const money = (cents) => currencyFormattedAmount((cents || 0) / 100);

const cardLabel = (payment) => {
  const brand = payment.card_brand ? payment.card_brand.charAt(0).toUpperCase() + payment.card_brand.slice(1) : 'Card';
  return payment.card_last4 ? `${brand} ending ${payment.card_last4}` : brand;
};

const STATUS_LABELS = {
  pending: 'Pending',
  succeeded: null,
  failed: 'Failed',
  canceled: 'Cancelled',
};

// What each logged event says, in the company's words.
const EVENT_LABELS = {
  charge_started: (e) => `Card payment of ${money(e.amount_cents)} started`,
  charge_pending: (e) => `Card payment of ${money(e.amount_cents)} is processing`,
  charge_succeeded: (e) => `Paid ${money(e.amount_cents)} by card`,
  charge_failed: (e) => `Card payment of ${money(e.amount_cents)} failed`,
  charge_canceled: (e) => `Card payment of ${money(e.amount_cents)} cancelled`,
  charge_error: () => 'Card payment couldn\'t start',
  invoice_created: () => 'Invoice created',
  invoice_balance: (e) => (e.amount_cents > 0 ? `${money(e.amount_cents)} still due on the invoice` : `Invoice overpaid by ${money(-e.amount_cents)}`),
  payment_added_to_invoice: (e) => `${money(e.amount_cents)} payment added to the invoice`,
  refund_issued: (e) => `Refunded ${money(e.amount_cents)}`,
  refund_failed: (e) => `Refund of ${money(e.amount_cents)} failed`,
  refund_added_to_invoice: (e) => `${money(e.amount_cents)} refund added to the invoice`,
};

const SOURCE_LABELS = { customer: 'Customer', stripe: 'Stripe', admin: 'Admin' };

const newRequestKey = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

@observer
class BookingCardPayments extends Component {
  @observable data = null;
  @observable loading = false;
  @observable failed = false;
  @observable refundOpen = false;
  @observable refundAmount = '';
  @observable refundReason = '';
  @observable refundConfirming = false;
  @observable refunding = false;
  requestKey = null;

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    const before = prevProps.attributes || {};
    const now = this.props.attributes || {};
    if (before.id !== now.id || before.amount_paid_cents !== now.amount_paid_cents ||
        before.invoice_id !== now.invoice_id || before.status !== now.status) {
      this.load();
    }
  }

  @action load() {
    const { attributes } = this.props;
    if (!attributes || !attributes.id || !attributes.card_paid) return;
    this.loading = true;
    Api.get(`/bookings/${attributes.id}/payments`)
      .then(action((response) => { this.data = response.data; this.failed = false; }))
      .catch(action(() => { this.failed = true; }))
      .then(action(() => { this.loading = false; }));
  }

  @action openRefund = () => {
    const invoice = this.data && this.data.invoice;
    // Prefill with what the invoice is overpaid by, when it is.
    const overpaid = invoice && invoice.due_cents < 0 ? Math.min(-invoice.due_cents, this.data.refundable_cents) : null;
    this.refundAmount = overpaid ? (overpaid / 100).toFixed(2) : '';
    this.refundReason = '';
    this.refundConfirming = false;
    this.refundOpen = true;
    this.requestKey = newRequestKey();
  };

  @action closeRefund = () => {
    this.refundOpen = false;
    this.refundConfirming = false;
  };

  get refundCents() {
    const value = parseFloat(String(this.refundAmount).replace(/[^0-9.]/g, ''));
    return value > 0 ? Math.round(value * 100) : 0;
  }

  @action reviewRefund = (event) => {
    if (event) event.preventDefault();
    const cents = this.refundCents;
    const max = (this.data && this.data.refundable_cents) || 0;
    if (!cents) {
      Toast.show({ icon: 'warning-sign', message: 'Enter an amount to refund.', intent: Intent.DANGER });
    } else if (cents > max) {
      Toast.show({ icon: 'warning-sign', message: `You can refund up to ${money(max)}.`, intent: Intent.DANGER });
    } else {
      this.refundConfirming = true;
    }
  };

  @action refund = () => {
    const { attributes, onChanged } = this.props;
    this.refunding = true;
    Api.post(`/bookings/${attributes.id}/refund`, {
      amount_cents: this.refundCents,
      reason: this.refundReason,
      request_key: this.requestKey,
    })
      .then(action((response) => {
        this.data = response.data;
        this.refundOpen = false;
        this.refundConfirming = false;
        Toast.show({ icon: 'tick', message: 'Refund sent to Stripe.', intent: Intent.SUCCESS });
        if (onChanged) onChanged();
      }))
      .catch(action((error) => {
        const body = error && error.response && error.response.data;
        if (body && body.payments) this.data = body;
        this.refundConfirming = false;
        this.requestKey = newRequestKey();
        const message = (body && (body.errors || body.error)) || 'The refund didn\'t go through. Try again.';
        Toast.show({ icon: 'warning-sign', message: Array.isArray(message) ? message.join(', ') : String(message), intent: Intent.DANGER });
      }))
      .then(action(() => { this.refunding = false; }));
  };

  renderInvoice() {
    const { attributes } = this.props;
    const invoice = this.data && this.data.invoice;
    if (invoice) {
      const due = invoice.due_cents;
      return (
        <div className={`bk-payments__invoice${due ? ' bk-payments__invoice--open' : ''}`}>
          <div>
            <a className="bk-payments__invoice-link" href={`/dashboard/invoices/${invoice.id}/show`}>Invoice {invoice.number}</a>
            <span className="bk-payments__muted">Total {money(invoice.total_cents)}</span>
          </div>
          <strong className="bk-payments__due">
            {due > 0 && `${money(due)} due`}
            {due < 0 && `Overpaid ${money(-due)}`}
            {!due && 'Paid in full'}
          </strong>
          {due > 0 && (
            <p className="bk-payments__hint">Send the invoice to the customer to collect the rest. They can pay it by card from the invoice.</p>
          )}
          {due < 0 && (
            <p className="bk-payments__hint">Refund the overpayment below.</p>
          )}
          {invoice.payments && invoice.payments.length > 0 && (
            <ul className="bk-payments__invoice-payments">
              {invoice.payments.map((payment) => (
                <li key={payment.id}>
                  <span>
                    Paid on the invoice · {payment.manual ? (payment.payment_method || 'Recorded by hand') : cardLabel(payment)}
                    {payment.paid_at ? `, ${moment(payment.paid_at).format('MMM D, YYYY')}` : ''}
                  </span>
                  <strong>{payment.amount_cents < 0 ? '−' : ''}{money(Math.abs(payment.amount_cents))}</strong>
                </li>
              ))}
            </ul>
          )}
        </div>
      );
    }
    const estimate = attributes.estimate_cents;
    const paid = attributes.amount_paid_cents || 0;
    const diff = estimate === null || estimate === undefined ? null : estimate - paid;
    return (
      <div className="bk-payments__invoice">
        <p className="bk-payments__hint">
          Confirming the booking creates its invoice on the job and moves these payments onto it.
          {diff > 0 && ` The booking now costs ${money(estimate)} with tax, so ${money(diff)} will be due on the invoice.`}
          {diff < 0 && ` The booking now costs ${money(estimate)} with tax, so the invoice will be overpaid by ${money(-diff)}.`}
        </p>
      </div>
    );
  }

  renderRefund() {
    const { attributes, isAdmin } = this.props;
    const refundable = (this.data && this.data.refundable_cents) || 0;
    if (!isAdmin || attributes.status !== 'confirmed' || refundable <= 0) return null;

    if (!this.refundOpen) {
      return (
        <div className="bk-payments__actions">
          <Button className="bk-btn--tint" icon="undo" onClick={this.openRefund}>Refund</Button>
        </div>
      );
    }

    if (this.refundConfirming) {
      return (
        <div className="bk-payments__refund" role="alertdialog" aria-label="Confirm refund">
          <p className="bk-payments__confirm">
            Refund {money(this.refundCents)} to the customer's card? Stripe sends it straight away, and it can't be undone.
          </p>
          <div className="bk-payments__actions">
            <Button intent={Intent.DANGER} loading={this.refunding} onClick={this.refund}>Refund {money(this.refundCents)}</Button>
            <Button disabled={this.refunding} onClick={() => { this.refundConfirming = false; }}>Back</Button>
          </div>
        </div>
      );
    }

    return (
      <form className="bk-payments__refund" onSubmit={this.reviewRefund}>
        <div className="bk-payments__fields">
          <label className="bk-field">
            <span className="bk-label">Amount</span>
            <input
              className="form-control"
              inputMode="decimal"
              value={this.refundAmount}
              onChange={(event) => { this.refundAmount = event.target.value; }}
              placeholder="0.00"
              autoFocus
            />
            <span className="bk-payments__muted">Up to {money(refundable)}</span>
          </label>
          <label className="bk-field bk-payments__reason">
            <span className="bk-label">Reason (optional)</span>
            <input
              className="form-control"
              value={this.refundReason}
              onChange={(event) => { this.refundReason = event.target.value; }}
              placeholder="Kept in the payment log"
            />
          </label>
        </div>
        <div className="bk-payments__actions">
          <Button type="submit" intent={Intent.PRIMARY}>Review refund</Button>
          <Button onClick={this.closeRefund}>Cancel</Button>
        </div>
      </form>
    );
  }

  render() {
    const { attributes } = this.props;
    if (!attributes || !attributes.card_paid) return null;

    const payments = (this.data && this.data.payments) || [];
    const events = ((this.data && this.data.events) || []).slice().reverse();

    return (
      <section className="bk-payments" aria-label="Card payments">
        <div className="bk-payments__head">
          <h2 className="bk-payments__title">Card payments</h2>
          <span className="bk-payments__paid">Paid {money(attributes.amount_paid_cents)}</span>
        </div>

        {this.loading && !this.data && <div className="bk-payments__loading"><Spinner size={20} /></div>}
        {this.failed && !this.data && (
          <p className="bk-payments__muted">The payments couldn't be loaded. <Button minimal small onClick={() => this.load()}>Try again</Button></p>
        )}

        {this.data && (
          <React.Fragment>
            <ul className="bk-payments__list">
              {payments.map((payment) => {
                const refund = payment.kind === 'refund';
                const status = STATUS_LABELS[payment.status];
                return (
                  <li key={payment.id} className={`bk-payments__row${status ? ' is-muted' : ''}`}>
                    <div className="bk-payments__text">
                      <span className="bk-payments__what">{refund ? 'Refund' : 'Payment'} · {cardLabel(payment)}</span>
                      <span className="bk-payments__muted">
                        {moment(payment.paid_at || payment.created_at).format('MMM D, YYYY h:mm A')}
                        {!refund && payment.tax_cents ? `, includes ${money(payment.tax_cents)} ${payment.tax_name || 'tax'}` : ''}
                        {payment.on_invoice ? ', on the invoice' : ''}
                      </span>
                    </div>
                    {status && <span className="bk-payments__status">{status}</span>}
                    <span className={`bk-payments__amount${refund ? ' is-refund' : ''}`}>{refund ? '−' : ''}{money(payment.amount_cents)}</span>
                  </li>
                );
              })}
            </ul>

            {this.renderInvoice()}
            {this.renderRefund()}

            {events.length > 0 && (
              <details className="bk-payments__log">
                <summary>Payment log ({events.length})</summary>
                <ol>
                  {events.map((event) => {
                    const label = EVENT_LABELS[event.event];
                    return (
                      <li key={event.id}>
                        <span className="bk-payments__log-what">{label ? label(event) : event.event.replace(/_/g, ' ')}</span>
                        {event.message && event.event !== 'invoice_balance' && <span className="bk-payments__log-message">{event.message}</span>}
                        <span className="bk-payments__muted">
                          {moment(event.created_at).format('MMM D, YYYY h:mm A')} · {event.actor_name || SOURCE_LABELS[event.source] || event.source}
                        </span>
                      </li>
                    );
                  })}
                </ol>
              </details>
            )}
          </React.Fragment>
        )}
      </section>
    );
  }
}

export default BookingCardPayments;
